'use client';

import { Check, ArrowRight } from 'lucide-react';
import Link from 'next/link';

export function CourseCTASection() {
  const courseFeatures = [
    'Three-electric system fundamentals',
    'High-voltage safety protocols',
    'Common fault diagnosis methods',
    'Repair tool operation guide',
    'Free basic Q&A support',
    'Discount on advanced training',
  ];

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[oklch(0.12_0.015_220)] via-[oklch(0.15_0.01_220)] to-[oklch(0.12_0.015_220)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(0,195,122,0.12)_0%,transparent_55%)]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative rounded-3xl overflow-hidden border border-[oklch(0.62_0.19_150)]/20 shadow-2xl shadow-black/40">
          {/* Glow border */}
          <div className="absolute inset-0 bg-gradient-to-r from-[oklch(0.62_0.19_150)]/10 via-transparent to-[oklch(0.85_0.12_140)]/10 pointer-events-none" />

          <div className="grid lg:grid-cols-5 gap-0">
            {/* Left: Content */}
            <div className="lg:col-span-3 p-8 lg:p-12 xl:p-14">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[oklch(0.62_0.19_150)]/15 border border-[oklch(0.62_0.19_150)]/30 text-[oklch(0.72_0.22_150)] text-sm font-medium mb-6">
                <span className="w-2 h-2 rounded-full bg-[oklch(0.72_0.22_150)] animate-pulse" />
                Entry-Level Training Course
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">
                Zero to EV Technician in <span className="text-glow">Just $300</span>
              </h2>
              <p className="text-lg text-muted-foreground mb-8 max-w-xl leading-relaxed">
                A standardized entry-level NEV repair course designed for traditional fuel vehicle repair shops.
                Master core skills in 2-4 weeks, quickly fill your local market gap, and capture the new energy market opportunity.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link
                  href="/services#training"
                  className="inline-flex items-center justify-center gap-2 px-8 h-12 rounded-xl bg-gradient-to-r from-[oklch(0.62_0.19_150)] to-[oklch(0.85_0.12_140)] text-[oklch(0.12_0.02_150)] font-semibold shadow-lg shadow-[oklch(0.62_0.19_150)]/30 hover:shadow-[oklch(0.62_0.19_150)]/50 transition-all"
                >
                  Learn About the Course
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <a
                  href="/success-stories"
                  className="inline-flex items-center justify-center gap-2 px-8 h-12 rounded-xl border border-[oklch(0.62_0.19_150)]/40 text-[oklch(0.72_0.22_150)] font-medium hover:bg-[oklch(0.62_0.19_150)]/10 transition-colors"
                >
                  View Student Stories
                </a>
              </div>
            </div>

            {/* Right: Price Card */}
            <div className="lg:col-span-2 relative bg-gradient-to-br from-[oklch(0.20_0.015_220)] to-[oklch(0.15_0.01_220)] p-8 lg:p-12 xl:p-14 border-t lg:border-t-0 lg:border-l border-[oklch(0.62_0.19_150)]/15">
              <div className="h-full flex flex-col">
                <div className="mb-6">
                  <span className="text-sm text-[oklch(0.72_0.22_150)] font-semibold uppercase tracking-wider">
                    Entry-Level Course
                  </span>
                  <div className="flex items-baseline gap-2 mt-2">
                    <span className="text-5xl font-extrabold text-glow number-glow">$300</span>
                    <span className="text-muted-foreground">/set</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">
                    One-time payment, lifetime access
                  </p>
                </div>

                <div className="space-y-3 mb-8 flex-grow">
                  {courseFeatures.map((feat) => (
                    <div key={feat} className="flex items-start gap-3">
                      <div className="w-5 h-5 rounded-full bg-[oklch(0.62_0.19_150)]/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check className="w-3 h-3 text-[oklch(0.72_0.22_150)]" />
                      </div>
                      <span className="text-sm text-foreground/85">{feat}</span>
                    </div>
                  ))}
                </div>

                <a
                  href="/contact"
                  className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-xl bg-gradient-to-r from-[oklch(0.62_0.19_150)] to-[oklch(0.85_0.12_140)] text-[oklch(0.12_0.02_150)] font-semibold shadow-lg shadow-[oklch(0.62_0.19_150)]/30 hover:shadow-[oklch(0.62_0.19_150)]/50 transition-all"
                >
                  Inquire Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
